import ipaImage from './ipa_image.png';
import { Modal, Spin, Button } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import ReactDOM from 'react-dom';

// Helper function to convert image URL to data URL
async function toDataURL(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return await new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}

// Helper function to wait for all assets to load
async function waitForAssets(doc) {
  // Wait for document complete
  if (doc.readyState !== 'complete') {
    await new Promise((r) =>
      doc.defaultView.addEventListener('load', r, { once: true })
    );
  }

  // Wait for all images to decode (handles cached images too)
  const imgs = Array.from(doc.images || []);
  await Promise.all(
    imgs.map((img) =>
      img.decode
        ? img.decode().catch(() =>
            new Promise((r) => {
              img.addEventListener('load', r, { once: true });
              img.addEventListener('error', r, { once: true });
            })
          )
        : new Promise((r) => {
            if (img.complete) return r();
            img.addEventListener('load', r, { once: true });
            img.addEventListener('error', r, { once: true });
          })
    )
  );

  // Wait for web fonts (if any)
  if (doc.fonts && doc.fonts.ready) {
    await doc.fonts.ready;
  }

  // One extra frame so layout fully settles
  await new Promise((r) =>
    requestAnimationFrame(() => requestAnimationFrame(r))
  );
}

export const printCartonLabels = async (printData, showHeader = true) => {
  try {
    // Create loading modal with "Sortir" button
    const loadingModalDiv = document.createElement('div');
    document.body.appendChild(loadingModalDiv);
    
    const closeLoadingModal = () => {
      ReactDOM.unmountComponentAtNode(loadingModalDiv);
      document.body.removeChild(loadingModalDiv);
    };
    
    ReactDOM.render(
      <Modal
        title="Preparing Labels"
        open={true}
        closable={true}
        maskClosable={false}
        footer={[
          <Button key="exit" onClick={closeLoadingModal}>
            Sortir
          </Button>
        ]}
      >
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Spin 
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            tip="Preparing carton labels..."
          />
        </div>
      </Modal>,
      loadingModalDiv
    );

    // Small delay to show the loading modal
    await new Promise(resolve => setTimeout(resolve, 500));

    const { 
      clientName, 
      address, 
      city, 
      phoneNumber, 
      transportMode, 
      commercial, 
      nbrCartons,
      REF_DEV,
      refBL
    } = printData;

    // Calculate pages needed (4 labels per page)
    const labelsPerPage = 4;
    const totalPages = Math.ceil(nbrCartons / labelsPerPage);
    
    // 1) Inline the logo so the HTML has a stable source
    const logoDataUrl = await toDataURL(ipaImage);

    // 2) Build HTML using the inlined logo and a base tag
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Étiquettes Cartons - ${refBL}</title>
        <base href="${window.location.origin}/">
        <style>
          
          
          body {
            margin: 0;
            padding: 0;
            font-family: Arial, sans-serif;
            font-size: 16px;
            line-height: 1.3;
            font-weight: bold;
          }
          
@page { size: A4 landscape; margin: 0; }
body { margin: 0; }

/* Full A4 area: 297 × 210 mm */
.page{
  width: 297mm;
  height: 210mm;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 2mm;          /* space between labels */
  padding: 0;
  box-sizing: border-box;
  page-break-after: always;
  margin: 0 auto;
}
.page:last-child{ page-break-after: auto; }

/* FIXED, SMALL LABEL SIZE + centered */
.label{
  border: 3px solid #000;
  background: #fff;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  width: 140mm;      /* fixed width */
  height: 95mm;      /* fixed height */
  place-self: center;  /* center within the grid cell */

  /* optional hard caps to prevent any overrides */
  min-width: 140mm;  max-width: 140mm;
  min-height: 95mm;  max-height: 95mm;
}


          
          .company-header {
            border-bottom: 3px solid #000;
            padding: 1mm;
            background: #fff;
            flex-shrink: 0;
          }
          
          .company-title {
            display: flex;
            align-items: center;
            margin-bottom: 1mm;
          }
          
          .logo-space {
            width: 30mm;
            height: 15mm;
            border: 2px solid #000;
            margin-right: 3mm;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #fff;
            overflow: hidden;
          }
          
          .logo-space img {
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
            margin-bottom: 1mm;
          }
          
          .company-name {
            font-size: 16px;
            font-weight: bold;
            text-transform: uppercase;
            line-height: 1.2;
          }
          
          .company-details {
            font-size: 12px;
            line-height: 1.3;
            font-weight: bold;
            margin-top: 1mm;
          }
          
          .label-body {
            flex: 1;
            padding: 1mm; /* Reduced padding for compact 4-label layout */
            display: flex;
            flex-direction: column;
            position: relative;
            min-height: 0;
          }
          
          .destinataire-section {
            flex: 0 1 auto;
            margin-bottom: auto;
            min-height: 0;
            padding-bottom: 1mm; /* Optimized bottom padding */
          }
          
          .destinataire-label {
            font-size: 12px;
            text-decoration: underline;
            color: #000;
            margin-right: 2mm;
          }
          
          .destinataire-label-inline {
            font-weight: bold;
            font-size: 18px;
            text-decoration: underline;
            color: #000;
            margin-right: 3mm;
          }
          
          .recipient-name {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 2mm;
            text-transform: uppercase;
            color: #000;
            background: #fff;
            padding: 1mm;
            border: 2px solid #000;
            min-height: 12mm; /* Height for 2 lines of 18px text */
            display: flex;
            align-items: flex-start;
            line-height: 1.3;
          }
          
          .recipient-phone {
            font-size: 16px;
            margin-bottom: 1px;
            font-weight: bold;
            color: #000;
            background: #fff;
            padding: 2px;
          }
          
          .recipient-address {
            font-size: 14px;
            margin-bottom: 1mm;
            font-weight: bold;
            color: #000;
            background: #fff;
            padding: 1mm;
            min-height: 10mm; /* Height for 2 lines of 14px text */
            display: flex;
            align-items: flex-start;
            line-height: 1.3;
            word-wrap: break-word;
            overflow-wrap: break-word;
            white-space: normal;
          }
          
          .recipient-city {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 1mm;
            text-transform: uppercase;
            color: #000;
            background: #fff;
            padding: 1mm;
            border: 2px solid #000;
            word-wrap: break-word; /* Allow text to wrap */
            overflow-wrap: break-word; /* Ensure long words break */
            white-space: normal; /* Allow text wrapping */
            min-height: 6mm; /* Height for 1 line of 16px text */
            display: flex;
            align-items: center;
            line-height: 1.3;
          }
          
          .recipient-city.long-text {
            font-size: 14px; /* Smaller font for long city names */
          }
          
          .bottom-section {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: auto;
            padding: 0.2mm;
            border-top: 2px solid #000;
            background: #fff;
            flex-shrink: 0;
            min-height: 8mm;
          }
          
          .info-section {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            flex: 1;
          }
          
          .carton-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            flex: 1;
          }
          
          .qr-section {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            flex: 1;
            padding: 0.5mm;
          }
          
          .carton-box {
            border: 3px solid #000;
            padding: 1mm;
            min-width: 45mm;
            text-align: center;
            background: #fff;
            margin-bottom: 1mm;
          }
          
          .carton-label {
            font-size: 12px;
            font-weight: bold;
            margin-bottom: 1mm;
            color: #000;
          }
          
          .carton-number {
            font-size: 20px;
            font-weight: bold;
            color: #000;
          }
          
          .ref-info {
            font-size: 12px;
            color: #000;
            font-weight: bold;
            margin-bottom: 0.3mm;
            background: #fff;
            padding: 0.3mm;
          }
          
          .mag-info {
            font-size: 10px;
            font-weight: bold;
            margin-top: 0.3mm;
            color: #000;
            background: #fff;
            padding: 0.3mm;
            border: 1px solid #000;
          }
          
          .barcode-space {
            width: 15mm;
            height: 15mm;
            border: 2px solid #000;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #fff;
            padding: 1mm;
          }
          
          .qr-code {
            width: 15mm;
            height: 15mm;
            background: #fff;
            border: 1px solid #000;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
          }
         
          .qr-code img {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }
          
          .date-stamp {
            font-size: 12px;
            color: #000;
            margin-bottom: 0.3mm;
            font-weight: bold;
            background: #fff;
            padding: 0.3mm;
          }
          
          .carton-line {
            font-size: 16px;
            font-weight: bold;
            color: #000;
          }
        </style>
      </head>
      <body>
        ${generatePagesHTML({ ...printData, logoSrc: logoDataUrl }, totalPages, labelsPerPage, showHeader)}
      </body>
      </html>
    `;
    
    // Close loading modal
    closeLoadingModal();

    // Create hidden iframe for printing (Electron-compatible)
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    
    document.body.appendChild(printFrame);
    
    // Use srcdoc + onload (more reliable than document.write + timeout)
    printFrame.onload = async () => {
      try {
        const doc = printFrame.contentDocument;
        await waitForAssets(doc);
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
      } catch (error) {
        console.warn('Print from iframe failed, trying direct print:', error);
        // Fallback: try direct window print
        window.print();
      } finally {
        // Clean up after the print dialog closes
        printFrame.contentWindow.onafterprint = () => {
          document.body.removeChild(printFrame);
        };
      }
    };
    printFrame.srcdoc = htmlContent;

    return { success: true };

  } catch (error) {
    console.error('Error generating carton labels:', error);
    Modal.error({
      title: 'Error',
      content: `Failed to generate labels: ${error.message}`,
    });
    throw new Error(`Failed to generate labels: ${error.message}`);
  }
};

// Thermal label version: one label per 101.6mm x 101.6mm page (Godex GE300, 4" x 4")
export const printCartonLabelsThermal = async (printData, showHeader = true) => {
  try {
    // Create loading modal with "Sortir" button
    const loadingModalDiv = document.createElement('div');
    document.body.appendChild(loadingModalDiv);

    const closeLoadingModal = () => {
      ReactDOM.unmountComponentAtNode(loadingModalDiv);
      document.body.removeChild(loadingModalDiv);
    };

    ReactDOM.render(
      <Modal
        title="Preparing Labels"
        open={true}
        closable={true}
        maskClosable={false}
        footer={[
          <Button key="exit" onClick={closeLoadingModal}>
            Sortir
          </Button>
        ]}
      >
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            tip="Preparing thermal labels..."
          />
        </div>
      </Modal>,
      loadingModalDiv
    );

    // Small delay to show the loading modal
    await new Promise(resolve => setTimeout(resolve, 500));

    const { nbrCartons, refBL } = printData;

    // 1) Inline the logo so the HTML has a stable source
    const logoDataUrl = await toDataURL(ipaImage);

    // 2) Build HTML: same label design, but one label per 101.6mm square page
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Étiquettes Cartons (Thermique) - ${refBL}</title>
        <base href="${window.location.origin}/">
        <style>
          body {
            margin: 0;
            padding: 0;
            font-family: Arial, sans-serif;
            font-size: 13px;
            line-height: 1.3;
            font-weight: bold;
          }

          @media print {
            img {
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }
          }

/* Godex GE300 - 4" x 4" label: 101.6 x 101.6 mm, one label per page */
@page { size: 101.6mm 101.6mm; margin: 0; }
body { margin: 0; }

.page{
  width: 101.6mm;
  height: 101.6mm;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  box-sizing: border-box;
  page-break-after: always;
  margin: 0;
  overflow: hidden;
}
.page:last-child{ page-break-after: auto; }

/* Label fills (almost) the whole label media, small safety margin */
.label{
  border: 2px solid #000;
  background: #fff;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  width: 97mm;
  height: 97mm;

  min-width: 97mm;  max-width: 97mm;
  min-height: 97mm; max-height: 97mm;
}

          .company-header {
            border-bottom: 2px solid #000;
            padding: 1mm;
            background: #fff;
            flex-shrink: 0;
          }

          .company-title {
            display: flex;
            align-items: center;
            margin-bottom: 1mm;
          }

          .logo-space {
            width: 24mm;
            height: 12mm;
            border: 2px solid #000;
            margin-right: 2mm;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #fff;
            overflow: hidden;
          }

          .logo-space img {
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
            margin-bottom: 1mm;
          }

          .company-name {
            font-size: 13px;
            font-weight: bold;
            text-transform: uppercase;
            line-height: 1.2;
          }

          .company-details {
            font-size: 10px;
            line-height: 1.3;
            font-weight: bold;
            margin-top: 1mm;
          }

          .label-body {
            flex: 1;
            padding: 1mm;
            display: flex;
            flex-direction: column;
            position: relative;
            min-height: 0;
          }

          .destinataire-section {
            flex: 0 1 auto;
            margin-bottom: auto;
            min-height: 0;
            padding-bottom: 1mm;
          }

          .destinataire-label {
            font-size: 10px;
            text-decoration: underline;
            color: #000;
            margin-right: 2mm;
          }

          .destinataire-label-inline {
            font-weight: bold;
            font-size: 15px;
            text-decoration: underline;
            color: #000;
            margin-right: 2mm;
          }

          .recipient-name {
            font-size: 15px;
            font-weight: bold;
            margin-bottom: 1.5mm;
            text-transform: uppercase;
            color: #000;
            background: #fff;
            padding: 1mm;
            border: 2px solid #000;
            min-height: 10mm;
            display: flex;
            align-items: flex-start;
            line-height: 1.3;
          }

          .recipient-phone {
            font-size: 13px;
            margin-bottom: 1px;
            font-weight: bold;
            color: #000;
            background: #fff;
            padding: 2px;
          }

          .recipient-address {
            font-size: 12px;
            margin-bottom: 1mm;
            font-weight: bold;
            color: #000;
            background: #fff;
            padding: 1mm;
            min-height: 9mm;
            display: flex;
            align-items: flex-start;
            line-height: 1.3;
            word-wrap: break-word;
            overflow-wrap: break-word;
            white-space: normal;
          }

          .recipient-city {
            font-size: 13px;
            font-weight: bold;
            margin-bottom: 1mm;
            text-transform: uppercase;
            color: #000;
            background: #fff;
            padding: 1mm;
            border: 2px solid #000;
            word-wrap: break-word;
            overflow-wrap: break-word;
            white-space: normal;
            min-height: 6mm;
            display: flex;
            align-items: center;
            line-height: 1.3;
          }

          .recipient-city.long-text {
            font-size: 12px;
          }

          .bottom-section {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: auto;
            padding: 0.2mm;
            border-top: 2px solid #000;
            background: #fff;
            flex-shrink: 0;
            min-height: 8mm;
          }

          .info-section {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            flex: 1;
          }

          .carton-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            flex: 1;
          }

          .qr-section {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            flex: 1;
            padding: 0.5mm;
          }

          .carton-box {
            border: 2px solid #000;
            padding: 1mm;
            min-width: 32mm;
            text-align: center;
            background: #fff;
            margin-bottom: 1mm;
          }

          .carton-label {
            font-size: 10px;
            font-weight: bold;
            margin-bottom: 1mm;
            color: #000;
          }

          .carton-number {
            font-size: 17px;
            font-weight: bold;
            color: #000;
          }

          .ref-info {
            font-size: 10px;
            color: #000;
            font-weight: bold;
            margin-bottom: 0.3mm;
            background: #fff;
            padding: 0.3mm;
          }

          .mag-info {
            font-size: 9px;
            font-weight: bold;
            margin-top: 0.3mm;
            color: #000;
            background: #fff;
            padding: 0.3mm;
            border: 1px solid #000;
          }

          .barcode-space {
            width: 13mm;
            height: 13mm;
            border: 2px solid #000;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #fff;
            padding: 1mm;
          }

          .qr-code {
            width: 13mm;
            height: 13mm;
            background: #fff;
            border: 1px solid #000;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
          }

          .qr-code img {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }

          .date-stamp {
            font-size: 10px;
            color: #000;
            margin-bottom: 0.3mm;
            font-weight: bold;
            background: #fff;
            padding: 0.3mm;
          }

          .carton-line {
            font-size: 13px;
            font-weight: bold;
            color: #000;
          }
        </style>
      </head>
      <body>
        ${generateThermalPagesHTML({ ...printData, logoSrc: logoDataUrl }, showHeader)}
      </body>
      </html>
    `;

    // Close loading modal
    closeLoadingModal();

    // Create hidden iframe for printing (Electron-compatible)
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';

    document.body.appendChild(printFrame);

    printFrame.onload = async () => {
      try {
        const doc = printFrame.contentDocument;
        await waitForAssets(doc);
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
      } catch (error) {
        console.warn('Print from iframe failed, trying direct print:', error);
        window.print();
      } finally {
        printFrame.contentWindow.onafterprint = () => {
          document.body.removeChild(printFrame);
        };
      }
    };
    printFrame.srcdoc = htmlContent;

    return { success: true };

  } catch (error) {
    console.error('Error generating thermal carton labels:', error);
    Modal.error({
      title: 'Error',
      content: `Failed to generate labels: ${error.message}`,
    });
    throw new Error(`Failed to generate labels: ${error.message}`);
  }
};

// Add a direct print function similar to FacturePrintUtility
export const printCartonLabelsDirect = async (printData, showHeader = true) => {
  try {
    const { 
      clientName, 
      address, 
      city, 
      phoneNumber, 
      transportMode, 
      commercial, 
      nbrCartons,
      REF_DEV,
      refBL 
    } = printData;

    // Calculate pages needed (4 labels per page)
    const labelsPerPage = 4;
    const totalPages = Math.ceil(nbrCartons / labelsPerPage);
    
    // 1) Inline the logo so the HTML has a stable source
    const logoDataUrl = await toDataURL(ipaImage);

    // 2) Build HTML using the inlined logo and a base tag
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Étiquettes Cartons - ${refBL}</title>
        <base href="${window.location.origin}/">
        <style>
         
          
          body {
            margin: 0;
            padding: 0;
            font-family: Arial, sans-serif;
            font-size: 16px;
            line-height: 1.3;
            font-weight: bold;
          }
          
          /* Debug: Uncomment next line to see page boundaries 
          .page { border: 2px dashed red !important; }
          */
          
          @media print {
            img { 
              print-color-adjust: exact; 
              -webkit-print-color-adjust: exact; 
            }
          }
          
@page { size: A4 landscape; margin: 0; }
body { margin: 0; }

/* Full A4 area: 297 × 210 mm */
.page{
  width: 297mm;
  height: 210mm;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 2mm;          /* space between labels */
  padding: 0;
  box-sizing: border-box;
  page-break-after: always;
  margin: 0 auto;
}
.page:last-child{ page-break-after: auto; }

/* FIXED, SMALL LABEL SIZE + centered */
.label{
  border: 3px solid #000;
  background: #fff;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  width: 140mm;      /* fixed width */
  height: 95mm;      /* fixed height */
  place-self: center;  /* center within the grid cell */

  /* optional hard caps to prevent any overrides */
  min-width: 140mm;  max-width: 140mm;
  min-height: 95mm;  max-height: 95mm;
}

          
          .company-header {
            border-bottom: 3px solid #000;
            padding: 1mm;
            background: #fff;
            flex-shrink: 0;
          }
          
          .company-title {
            display: flex;
            align-items: center;
            margin-bottom: 1mm;
          }
          
          .logo-space {
            width: 30mm;
            height: 15mm;
            border: 2px solid #000;
            margin-right: 3mm;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #fff;
            overflow: hidden;
          }
          
          .logo-space img {
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
            margin-bottom: 1mm;
          }
          
          .company-name {
            font-size: 16px;
            font-weight: bold;
            text-transform: uppercase;
            line-height: 1.2;
          }
          
          .company-details {
            font-size: 12px;
            line-height: 1.3;
            font-weight: bold;
            margin-top: 1mm;
          }
          
          .label-body {
            flex: 1;
            padding: 2mm; /* Slightly reduced padding */
            display: flex;
            flex-direction: column;
            position: relative;
            min-height: 0;
          }
          
          .destinataire-section {
            flex: 0 1 auto;
            margin-bottom: auto;
            min-height: 0;
            padding-bottom: 2mm; /* Add some bottom padding */
          }
          
          .destinataire-label {
            font-size: 12px;
            text-decoration: underline;
            color: #000;
            margin-right: 2mm;
          }
          
          .destinataire-label-inline {
            font-weight: bold;
            font-size: 18px;
            text-decoration: underline;
            color: #000;
            margin-right: 3mm;
          }
          
          .recipient-name {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 1mm;
            text-transform: uppercase;
            color: #000;
            background: #fff;
            padding: 1mm;
            border: 2px solid #000;
            min-height: 6mm; /* Height for 1 line of 16px text */
            display: flex;
            align-items: center;
            line-height: 1.3;
          }
          
          .recipient-phone {
            font-size: 16px;
            margin-bottom: 1px;
            font-weight: bold;
            color: #000;
            background: #fff;
            padding: 2px;
          }
          
          .recipient-address {
            font-size: 14px;
            margin-bottom: 1mm;
            font-weight: bold;
            color: #000;
            background: #fff;
            padding: 1mm;
            min-height: 10mm; /* Height for 2 lines of 14px text */
            display: flex;
            align-items: flex-start;
            line-height: 1.3;
            word-wrap: break-word;
            overflow-wrap: break-word;
            white-space: normal;
          }
          
          .recipient-city {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 1mm;
            text-transform: uppercase;
            color: #000;
            background: #fff;
            padding: 1mm;
            border: 2px solid #000;
            word-wrap: break-word; /* Allow text to wrap */
            overflow-wrap: break-word; /* Ensure long words break */
            white-space: normal; /* Allow text wrapping */
            min-height: 6mm; /* Height for 1 line of 16px text */
            display: flex;
            align-items: center;
            line-height: 1.3;
          }
          
          .recipient-city.long-text {
            font-size: 14px; /* Smaller font for long city names */
          }
          
          .bottom-section {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: auto;
            padding: 0.2mm;
            border-top: 2px solid #000;
            background: #fff;
            flex-shrink: 0;
            min-height: 8mm;
          }
          
          .info-section {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            flex: 1;
          }
          
          .carton-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            flex: 1;
          }
          
          .qr-section {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            flex: 1;
            padding: 0.5mm;
          }
          
          .carton-box {
            border: 3px solid #000;
            padding: 3mm;
            min-width: 45mm;
            text-align: center;
            background: #fff;
            margin-bottom: 1mm;
          }
          
          .carton-label {
            font-size: 12px;
            font-weight: bold;
            margin-bottom: 1mm;
            color: #000;
          }
          
          .carton-number {
            font-size: 20px;
            font-weight: bold;
            color: #000;
          }
          
          .ref-info {
            font-size: 12px;
            color: #000;
            font-weight: bold;
            margin-bottom: 0.3mm;
            background: #fff;
            padding: 0.3mm;
          }
          
          .mag-info {
            font-size: 10px;
            font-weight: bold;
            margin-top: 0.3mm;
            color: #000;
            background: #fff;
            padding: 0.3mm;
            border: 1px solid #000;
          }
          
          .barcode-space {
            width: 15mm;
            height: 15mm;
            border: 2px solid #000;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #fff;
            padding: 1mm;
          }
          
          .qr-code {
            width: 15mm;
            height: 15mm;
            background: #fff;
            border: 1px solid #000;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
          }
         
          .qr-code img {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }
          
          .date-stamp {
            font-size: 12px;
            color: #000;
            margin-bottom: 0.3mm;
            font-weight: bold;
            background: #fff;
            padding: 0.3mm;
          }
          
          .carton-line {
            font-size: 16px;
            font-weight: bold;
            color: #000;
          }
        </style>
      </head>
      <body>
        ${generatePagesHTML({ ...printData, logoSrc: logoDataUrl }, totalPages, labelsPerPage, showHeader)}
      </body>
      </html>
    `;

    // Create hidden iframe for printing (Electron-compatible)
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    
    document.body.appendChild(printFrame);
    
    // Use srcdoc + onload (more reliable than document.write + timeout)
    printFrame.onload = async () => {
      try {
        const doc = printFrame.contentDocument;
        await waitForAssets(doc);
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
      } catch (error) {
        console.warn('Print from iframe failed, trying direct print:', error);
        // Fallback: try direct window print
        window.print();
      } finally {
        // Clean up after the print dialog closes
        printFrame.contentWindow.onafterprint = () => {
          document.body.removeChild(printFrame);
        };
      }
    };
    printFrame.srcdoc = htmlContent;
  } catch (error) {
    console.error('Error generating carton labels for direct print:', error);
    throw new Error(`Failed to generate labels: ${error.message}`);
  }
};

const generatePagesHTML = (printData, totalPages, labelsPerPage, showHeader) => {
  const { 
    clientName, 
    address, 
    city, 
    phoneNumber, 
    transportMode, 
    commercial, 
    nbrCartons,
    REF_DEV,
    refBL,
    logoSrc
  } = printData;
  
  let html = '';
  let labelCounter = 1;
  
  for (let page = 0; page < totalPages; page++) {
    const labelsOnThisPage = Math.min(labelsPerPage, nbrCartons - (page * labelsPerPage));
    
    html += `<div class="page">`;
    
    for (let i = 0; i < labelsOnThisPage; i++) {
      html += generateLabelHTML({
        clientName,
        address,
        city,
        phoneNumber,
        transportMode,
        commercial,
        REF_DEV,
        refBL,
        cartonNumber: labelCounter,
        totalCartons: nbrCartons,
        showHeader,
        logoSrc
      });
      labelCounter++;
    }
    
    html += '</div>';
  }
  
  return html;
};

// One label per page (thermal 101.6 x 101.6 mm format)
const generateThermalPagesHTML = (printData, showHeader) => {
  const {
    clientName,
    address,
    city,
    phoneNumber,
    transportMode,
    commercial,
    nbrCartons,
    REF_DEV,
    refBL,
    logoSrc
  } = printData;

  let html = '';

  for (let cartonNumber = 1; cartonNumber <= nbrCartons; cartonNumber++) {
    html += `<div class="page">`;
    html += generateLabelHTML({
      clientName,
      address,
      city,
      phoneNumber,
      transportMode,
      commercial,
      REF_DEV,
      refBL,
      cartonNumber,
      totalCartons: nbrCartons,
      showHeader,
      logoSrc
    });
    html += '</div>';
  }

  return html;
};

const generateQRPattern = () => {
  // Generate QR code that points to https://ihssanpa.carrd.co/
  const qrCodeUrl = 'https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=' + encodeURIComponent('https://ihssanpa.carrd.co/');
  
  return `<img src="${qrCodeUrl}" alt="QR Code" style="width: 100%; height: 100%; object-fit: contain;" />`;
};

const generateLabelHTML = (labelData) => {
  const {
    clientName,
    address,
    city,
    phoneNumber,
    transportMode,
    commercial,
    REF_DEV,
    refBL,
    cartonNumber,
    totalCartons,
    showHeader,
    logoSrc
  } = labelData;
  
  const currentDate = new Date().toLocaleDateString('fr-FR');
  
  // Check if city text is long and add appropriate class
  const cityClass = city && city.length > 15 ? 'recipient-city long-text' : 'recipient-city';
  
  return `
    <div class="label">
      <div class="company-header">
        ${showHeader ? `
        <div class="company-title">
          <div class="logo-space">
            <img src="${logoSrc}" alt="IPA Logo" />
          </div>
          <div>
            <div class="company-name">IHSSAN PIECES AUTOS - CASABLANCA</div>
            <div class="company-details">
              N° 20 Rue Khouribga, Galerie Chems<br>
              Tél : 0522-307220 (LG) &nbsp;&nbsp; Fax : 0522-306557
            </div>
          </div>
        </div>
        ` : `
        <div class="company-title">
          <div class="logo-space" style="background: white; border: none;">
            &nbsp;
          </div>
          <div>
            <div class="company-name" style="color: white;">
              &nbsp;
            </div>
            <div class="company-details" style="color: white;">
              &nbsp;<br>
              &nbsp;
            </div>
          </div>
        </div>
        `}
      </div>
      
      <div class="label-body">
        <div class="destinataire-section">
          <div class="recipient-name"><span class="destinataire-label">DESTINATAIRE: </span> ${clientName || 'N/A'}</div>
          
          ${phoneNumber ? `<div class="recipient-phone">Tél : ${phoneNumber}</div>` : ''}
          
          <div class="recipient-address">Adresse : ${address || 'N/A'}</div>
          
          <div class="${cityClass}">Ville : ${city || 'N/A'}</div>
        </div>
        
        <div class="bottom-section">
          <div class="info-section">
            ${commercial ? `<div class="ref-info">${commercial}</div>` : ''}
            <div class="date-stamp">Date : ${currentDate}</div>
            <div class="mag-info">MAG 3</div>
          </div>
          
          <div class="carton-section">
            <div class="carton-box">
              <div class="carton-line">CARTON N°: ${cartonNumber} / ${totalCartons}</div>
            </div>
            ${transportMode ? `<div class="ref-info" style="text-align: center;">Transport : ${transportMode}</div>` : ''}
          </div>
          
          <div class="qr-section" style="display: flex; flex-direction: column; justify-content: flex-end; align-items: flex-end;">
            <div class="barcode-space">
              <div class="qr-code">
                ${generateQRPattern()}
              </div>
            </div>
            <div class="REF_DEV-text" style="font-size: 14px; font-weight: bold; color: #000; margin-top: 1mm; margin-right: 0.5mm; text-align: right;">
              ${REF_DEV && REF_DEV.length > 2 ? REF_DEV.substring(2) : ''}
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
};

export default printCartonLabels;